import React, { useCallback, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { isEqual, isNull, isUndefined } from 'lodash/fp';

import { Form } from '../../../shared/icons';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import styles from '../Workflow.module.scss';
import { getReportAttachmentTabs, ReportEmailAttachment, ReportEmailType } from '../store';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { WizardTab } from '../../../shared/wizard/WizardTab';
import { useAppSelector } from '../../../../hooks/react-redux';
import { DocumentReportTemplate, getAllReportTemplates as getAllDocumentReportTemplates } from '../../../documents/reporting/store';
import { getAllReportTemplates as getAllOpinionReportTemplates, OpinionReportTemplate } from '../../../opinions/reporting/store';
import { getPortfolioAnalyticsTemplates, PortfolioAnalysisTemplate } from '../../portfolio-analytics/store';
import { DocumentTemplate } from '../../../documents/reporting/templates/DocumentTemplate';
import { OpinionTemplate } from '../../../opinions/reporting/OpinionTemplate';
import { PortfolioAnalyticsTemplate } from '../../portfolio-analytics/PortfolioAnalyticsTemplate';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Button } from '../../../shared/button/Button';
import { Icon } from '../../../shared/icon/Icon';
import { Paperclip } from '../../../shared/icons/Paperclip';

interface ReportTabsProps {
    reportTypes: ReportEmailType[];
    selected: ReportEmailType | undefined;
    select: (reportType: ReportEmailType) => void;
}

export const ReportTabs: React.FC<ReportTabsProps> = ({ reportTypes, selected, select }) => {
    const tabs = useMemo(() => reportTypes.map(tab => ({
        label: capitaliseStartLetter(tab),
        selected: tab === selected,
        onClick: () => select(tab),
    })), [reportTypes, selected, select]);

    return (
        <>
            {reportTypes.length > 1 && <div className={styles.tabWrapper}>
                {tabs.map(({ label, onClick, selected }) => (
                    <WizardTab key={label} label={label} onClick={onClick} selected={selected} />
                ))}
            </div>}
        </>
    );
};

interface ReportAttachmentModalProps {
    isOpen: boolean;
    attachment: ReportEmailAttachment | null;
    updateReportAttachment: (attachment: ReportEmailAttachment | null) => void;
    closeModal: () => void;
    testId: string;
}

export const ReportAttachmentModal: React.FC<ReportAttachmentModalProps> = ({ attachment, isOpen, testId, updateReportAttachment, closeModal }) => {
    const reportTypes = useAppSelector(getReportAttachmentTabs);
    const [selectedReportType, setSelectedReportType] = useState<ReportEmailType | undefined>(reportTypes[0]);

    const documentReportTemplates = useAppSelector(getAllDocumentReportTemplates);
    const opinionReportTemplates = useAppSelector(getAllOpinionReportTemplates);
    const portfolioAnalyticsReportTemplates = useAppSelector(getPortfolioAnalyticsTemplates);

    const getIsSelected = useCallback((templateAttachment: ReportEmailAttachment) => !isNull(attachment) && isEqual(templateAttachment, attachment), [attachment]);

    const selectReportTemplate = useCallback((templateAttachment: ReportEmailAttachment) => {
        const shouldDeselect = getIsSelected(templateAttachment);
        if (shouldDeselect) {
            updateReportAttachment(null);
            return;
        }
        updateReportAttachment(templateAttachment);
    }, [getIsSelected, updateReportAttachment]);

    const reportsHeight = useMemo(() => reportTypes.length > 1 ? 'calc(100% - 43px)' : '100%', [reportTypes]);

    const reportTemplates = useMemo(() => {
        switch (selectedReportType) {
            case ReportEmailType.DOCUMENT:
                return documentReportTemplates;
            case ReportEmailType.OPINION:
                return opinionReportTemplates;
            case ReportEmailType.PORTFOLIO_ANALYTICS:
                return portfolioAnalyticsReportTemplates;
            default:
                return [];
        }
    }, [selectedReportType, documentReportTemplates, opinionReportTemplates, portfolioAnalyticsReportTemplates]);

    const attachedPlaceholder = useMemo(() => (
        <div className={styles.selectedCover}>
            <div className={styles.paperclipWrapper}><Icon icon={Paperclip} fontSize={50} /></div>
            <div>ATTACHED</div>
        </div>
    ), []);

    const content = useMemo(() => {
        if (reportTemplates.length === 0) {
            return (
                <div className={styles.contentWrapper}>You do not have any {!isUndefined(selectedReportType) ? `${capitaliseStartLetter(selectedReportType)} ` : ''}templates</div>
            );
        }
        switch (selectedReportType) {
            case ReportEmailType.DOCUMENT:
                return (
                    <div className={styles.contentWrapper}>
                        {reportTemplates.map(template => {
                            const documentReportTemplate = template as DocumentReportTemplate;
                            const templateAttachment = { reportType: ReportEmailType.DOCUMENT, templateId: documentReportTemplate.reportTemplateId, templateName: documentReportTemplate.name };
                            const isSelected = getIsSelected(templateAttachment);
                            return (
                                <div className={styles.templateWrapper} key={documentReportTemplate.reportTemplateId}>
                                    <DocumentTemplate
                                        template={documentReportTemplate}
                                        openTemplate={() => selectReportTemplate(templateAttachment)}
                                        testId='workflow-document-report-attachment'
                                        canMakeTemplateChanges={false}
                                        showButtons={false}
                                        selectOnSingleClick
                                    />
                                    {isSelected && attachedPlaceholder}
                                </div>
                            );
                        })}
                    </div>
                );
            case ReportEmailType.OPINION:
                return (
                    <div className={styles.contentWrapper}>
                        {reportTemplates.map(template => {
                            const opinionReportTemplate = template as OpinionReportTemplate;
                            const templateAttachment = { reportType: ReportEmailType.OPINION, templateId: opinionReportTemplate.opinionReportTemplateId, templateName: opinionReportTemplate.name };
                            const isSelected = getIsSelected(templateAttachment);
                            return (
                                <div className={styles.templateWrapper} key={opinionReportTemplate.opinionReportTemplateId}>
                                    <OpinionTemplate
                                        template={opinionReportTemplate}
                                        openTemplate={() => selectReportTemplate(templateAttachment)}
                                        testId='workflow-opinion-report-attachment'
                                        canMakeTemplateChanges={false}
                                        showButtons={false}
                                        selectOnSingleClick
                                    />
                                    {isSelected && attachedPlaceholder}
                                </div>
                            );
                        })}
                    </div>
                );
            case ReportEmailType.PORTFOLIO_ANALYTICS:
                return (
                    <div className={styles.contentWrapper}>
                        {reportTemplates.map(template => {
                            const portfolioAnalysisTemplate = template as PortfolioAnalysisTemplate;
                            const templateAttachment = { reportType: ReportEmailType.PORTFOLIO_ANALYTICS, templateId: portfolioAnalysisTemplate.portfolioAnalysisTemplateId, templateName: portfolioAnalysisTemplate.templateName };
                            const isSelected = getIsSelected(templateAttachment);
                            return (
                                <div className={styles.templateWrapper} key={portfolioAnalysisTemplate.portfolioAnalysisTemplateId}>
                                    <PortfolioAnalyticsTemplate
                                        template={portfolioAnalysisTemplate}
                                        selectTemplate={() => selectReportTemplate(templateAttachment)}
                                        selectOnSingleClick
                                    />
                                    {isSelected && attachedPlaceholder}
                                </div>
                            );
                        })}
                    </div>
                );
            default:
                return null;
        }
    }, [reportTemplates, selectedReportType, selectReportTemplate, getIsSelected, attachedPlaceholder]);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            style={{ overlay: { display: 'flex', zIndex: 11 } }}
            className={styles.reportAttachmentModal}
            ariaHideApp={false}
        >
            <div className={styles.reportAttachmentModalWrapper} data-testid={`${testId}-wrapper`}>
                <ModalHeader label='Select Report Template as Attachment' icon={Form} />
                <div className={styles.reportAttachmentWrapper}>
                    <ReportTabs reportTypes={reportTypes} selected={selectedReportType} select={setSelectedReportType} />
                    <div className={styles.reportAttachmentOptions} style={{ height: reportsHeight }}>
                        <Scrollable>
                            {content}
                        </Scrollable>
                    </div>
                </div>
                <div className={styles.buttonWrapper}>
                    <Button label='Close' onClick={closeModal} />
                </div>
            </div>
        </Modal>
    );
};

